import { CircularProgress, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { noop } from 'lodash'
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'

import { AGENT_ID_QUERY_KEY } from '~/features/agents/constants'
import { useSetAgentId } from '~/features/agents/hooks/useSetAgentId'
import { SignoutContext } from '~/features/auth/constants'
import { TSignoutContext } from '~/features/auth/models'
import useRemountEmbeddedChatOnUserIdChange from '~/features/chatbot/hooks/useRemountEmbeddedChatOnUserIdChange'
import asyncExportJobsStore from '~/features/exports/store/asyncExportJobsStore'
import { sessionStore, store } from '~/store'

type TProps = PropsWithChildren

function SignoutContextProvider({ children = null }: TProps) {
  const queryClient = useQueryClient()
  const setAgentId = useSetAgentId()

  const [searchParams] = useSearchParams()
  const searchParamsAgentId = searchParams.get(AGENT_ID_QUERY_KEY)

  const resetSessionStore = sessionStore((state) => state.resetStore)
  const resetStore = store((state) => state.resetStore)

  const [isSigningOut, setIsSigningOut] = useState(false)
  const [isRemovingAgentId, setIsRemovingAgentId] = useState(false)

  useRemountEmbeddedChatOnUserIdChange()

  const signoutContextValue: TSignoutContext = useMemo(
    () => ({
      isSigningOut,
      setIsSigningOut,
    }),
    [isSigningOut],
  )

  const handleResetState = useCallback(async () => {
    await queryClient.cancelQueries()

    setAgentId({ agentId: undefined })

    asyncExportJobsStore.getState().clearJobs()

    resetStore([
      'user.signInRedirectPath',
      'user.lastSelectedAgentId',
      'adminPortal.recentLoginAsUsers',
    ])
    resetSessionStore()

    queryClient.clear()
  }, [queryClient, resetSessionStore, resetStore, setAgentId])

  useEffect(() => {
    if (isSigningOut && !isRemovingAgentId) {
      handleResetState()
        .then(noop)
        // eslint-disable-next-line no-console
        .catch((error) => console.error('Error resetting state:', error))
        .finally(() => setIsRemovingAgentId(true))
    }
  }, [handleResetState, isRemovingAgentId, isSigningOut])

  useEffect(() => {
    if (!isSigningOut) return

    if (isRemovingAgentId) {
      if (searchParamsAgentId) {
        setAgentId({ agentId: undefined })
      } else {
        setIsRemovingAgentId(false)
        setIsSigningOut(false)
      }
    }
  }, [isRemovingAgentId, isSigningOut, searchParamsAgentId, setAgentId])

  return (
    <SignoutContext.Provider value={signoutContextValue}>
      {isSigningOut ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ width: '100vw', height: '100vh' }}
        >
          <CircularProgress />
        </Stack>
      ) : (
        children
      )}
    </SignoutContext.Provider>
  )
}

export default SignoutContextProvider
